import { useRef, useCallback } from 'react'

import { useFloorStore } from '../../../../map-editor-shared/store/floorStore'
import { useCsvRuntimeStore } from '../../../../map-editor-features/csv/model/csvStore'
import type {
  MapData as ApiMapData
} from '../../../../map-editor-shared/api/types/mapData'

import type { UseFloorManagementProps, UseFloorManagementReturn } from './types'
import { useFloorAutoSave } from './useFloorAutoSave'
import { useFloorSwitching } from './useFloorSwitching'
import { useFloorRestoration } from './useFloorRestoration'
import { useFloorExport } from './useFloorExport'

export type { UseFloorManagementProps, UseFloorManagementReturn }

/**
 * Custom hook for managing floor switching, auto-save, and floor-related state
 *
 * Handles:
 * - Floor switching with state restoration
 * - Two-tier auto-save (localStorage + server)
 * - CSV state management per floor
 * - Multi-floor data preparation for export
 * - Race condition prevention during rapid switches
 *
 * @param props - Configuration and dependencies
 * @returns Floor management state and handlers
 */
export function useFloorManagement(props: UseFloorManagementProps): UseFloorManagementReturn {
  const {
    graph,
    paper,
    selectedElementId,
    projectId,
    currentLot,
    currentLotData,
    updateMapDataMutation,
    editorMapData,
    onFloorSwitchStart,
    onFloorSwitchComplete,
  } = props

  const { updateFloorMapData } = useFloorStore()

  const savedBeforeFloorChangeRef = useRef<boolean>(false)

  // --- Sub-hooks ---

  const { isRestoring, renderScale, loadedFileName, handleRenderScaleChange } =
    useFloorSwitching({
      graph,
      paper,
      currentLot,
      editorMapData,
      onFloorSwitchStart,
      onFloorSwitchComplete,
      setObjectsByLayer: props.setObjectsByLayer,
      setElementCount: props.setElementCount,
    })

  useFloorAutoSave({
    graph,
    paper,
    selectedElementId,
    projectId,
    currentLot,
    currentLotData,
    renderScale,
    updateMapDataMutation,
  })

  const { restoreFloorCSVState } = useFloorRestoration()

  const { prepareFloorsForExport } = useFloorExport({ graph })

  // ======================
  // Handler: Before floor change (save current floor state)
  // ======================
  const handleBeforeFloorChange = useCallback(
    async (floorIdToSave: string) => {
      // floorIdToSave is the floor we're leaving (the one we need to save)
      // This is passed from FloorTabs as the current floor before switching
      if (!floorIdToSave || !graph) {
        return
      }

      try {
        savedBeforeFloorChangeRef.current = true

        const csvSnap = useCsvRuntimeStore.getState().getCsvRuntimeForFloor(floorIdToSave)

        // CRITICAL: Use floorIdToSave so we persist the floor being left, not a stale currentFloor.
        updateFloorMapData(floorIdToSave, {
          graphJson: graph.toJSON(),
          csvRawData: csvSnap.rawData,
          csvParsedData: csvSnap.parsedData,
          csvGroupedLayers: csvSnap.groupedLayers,
          csvSelectedLayers: Array.from(csvSnap.selectedLayers),
          csvFileName: csvSnap.uploadState.status === 'parsed' ? csvSnap.uploadState.fileName : null,
          renderScale,
        })

        // Also save to server immediately (don't wait for debounce)
        // CRITICAL FIX: Get freshest floors from Zustand state after updateFloorMapData
        // The 'floors' variable from hook scope might be stale
        const freshFloors = useFloorStore.getState().floors
        const projectFloors = freshFloors.filter(f => f.lotId === currentLot)
        const floorsPayload = projectFloors.map(floor => ({
          name: floor.name,
          order: floor.order,
          graphJson: floor.mapData?.graphJson,
        })).filter(f => f.graphJson)

        if (floorsPayload.length > 0 && projectId && currentLot) {
          const mapDataPayload: ApiMapData = {
            floors: floorsPayload,
            template_id: currentLotData?.templateId
          }

          // FIXED: Pass API MapData instead of Store MapData
          await updateMapDataMutation.mutateAsync({
            projectId,
            mapData: mapDataPayload,
          })
        }

      } catch (error) {
      }
    },
    // Note: Removed 'currentFloor' and 'floors' from dependencies since we use
    // the passed parameter and useFloorStore.getState() for freshest state
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [graph, renderScale, projectId, currentLot, currentLotData?.templateId, updateFloorMapData, updateMapDataMutation]
  )

  // ======================
  // Return hook interface
  // ======================
  return {
    // State
    isRestoring,
    renderScale,
    loadedFileName,

    // Handlers
    handleBeforeFloorChange,
    handleRenderScaleChange,

    // Helper functions
    restoreFloorCSVState,
    prepareFloorsForExport,
  }
}
